/**
 * Fuseaux horaires : UNIQUE endroit du monorepo frontend qui convertit entre
 * un instant UTC et l'heure murale d'un fuseau IANA.
 *
 * Pourquoi ce module : `Intl` (donc `date-fns-tz`, `toLocale*String`...) lit
 * la base de fuseaux du navigateur. Une tablette ou un poste dont la base est
 * perimee donne une heure fausse (ex. Maroc repasse a UTC+0 permanent le
 * 2026-09-20, tzdata 2026c). Ici les regles viennent de donnees EMBARQUEES
 * dans le bundle (moment-timezone, build a plage reduite) : le resultat ne
 * depend ni du fuseau ni de la base de fuseaux de la machine qui affiche.
 *
 * Regles :
 * - la bibliotheque de fuseaux n'est importee QUE dans ce fichier (garde-fou :
 *   `__tests__/timezone-guard.test.ts`) ;
 * - le serveur stocke et envoie de l'UTC : on convertit seulement a
 *   l'affichage et a la saisie.
 *
 * Couverture des donnees embarquees : environ 2021 -> 2031 (fenetre calculee
 * a partir des donnees elles-memes). En dehors de cette fenetre les donnees
 * sont tronquees (decalage fige) : on retombe alors sur `Intl`, fiable pour
 * les dates anciennes. Idem pour un fuseau inconnu des donnees embarquees.
 */

import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range';

export type InstantInput = Date | string | number;

/** Heure murale (calendrier + horloge) sans fuseau. `month` : 1-12. */
export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;

// ---------------------------------------------------------------------------
// Donnees embarquees
// ---------------------------------------------------------------------------

/** Version tzdata des donnees embarquees (ex. "2026e"). */
export function getTimezoneDataVersion(): string {
  return moment.tz.dataVersion;
}

let embeddedWindow: { from: number; to: number } | null = null;

/**
 * Fenetre [from, to] dans laquelle les donnees embarquees sont completes.
 * Le build a plage reduite ne garde que les transitions d'une plage d'annees :
 * on la deduit des transitions presentes (premiere et derniere, tous fuseaux
 * confondus), avec un jour de marge a chaque bout.
 */
function getEmbeddedWindow(): { from: number; to: number } {
  if (embeddedWindow) return embeddedWindow;
  let first = Infinity;
  let last = -Infinity;
  for (const name of moment.tz.names()) {
    const zone = moment.tz.zone(name);
    if (!zone) continue;
    for (const until of zone.untils) {
      if (!Number.isFinite(until)) continue;
      if (until < first) first = until;
      if (until > last) last = until;
    }
  }
  embeddedWindow = {
    from: Date.UTC(new Date(first).getUTCFullYear(), 0, 2),
    to: Date.UTC(new Date(last).getUTCFullYear(), 11, 30),
  };
  return embeddedWindow;
}

/** Periode couverte par les donnees embarquees (instants UTC). */
export function getTimezoneDataCoverage(): { from: Date; to: Date } {
  const { from, to } = getEmbeddedWindow();
  return { from: new Date(from), to: new Date(to) };
}

// ---------------------------------------------------------------------------
// Repli hors fenetre : base de fuseaux du navigateur
// ---------------------------------------------------------------------------

const intlFormatters = new Map<string, Intl.DateTimeFormat | null>();

function getIntlFormatter(timezone: string): Intl.DateTimeFormat | null {
  if (!intlFormatters.has(timezone)) {
    let formatter: Intl.DateTimeFormat | null = null;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      });
    } catch {
      // Fuseau invalide : on le signale une seule fois et on retombe sur UTC.
      console.warn(`[timezone] Fuseau inconnu "${timezone}" : UTC utilise a la place.`);
    }
    intlFormatters.set(timezone, formatter);
  }
  return intlFormatters.get(timezone) ?? null;
}

function intlOffsetMinutes(timezone: string, ms: number): number {
  const formatter = getIntlFormatter(timezone);
  if (!formatter) return 0;
  const parts: Record<string, number> = {};
  for (const part of formatter.formatToParts(new Date(ms))) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  const wallAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const flooredToSecond = Math.floor(ms / 1000) * 1000;
  return Math.round((wallAsUtc - flooredToSecond) / MS_PER_MINUTE);
}

// ---------------------------------------------------------------------------
// Decalage
// ---------------------------------------------------------------------------

function toMs(instant: InstantInput): number {
  if (instant instanceof Date) return instant.getTime();
  if (typeof instant === 'number') return instant;
  return new Date(instant).getTime();
}

/** Decalage (minutes a l'EST d'UTC) du fuseau a l'instant `ms`. */
function offsetMinutesAt(timezone: string, ms: number): number {
  const zone = moment.tz.zone(timezone);
  if (zone) {
    const { from, to } = getEmbeddedWindow();
    // Hors fenetre les donnees embarquees sont tronquees : repli plus bas.
    if (ms >= from && ms <= to) {
      // moment compte les minutes a l'OUEST d'UTC : on inverse le signe
      // (`0 - x` pour ne jamais produire -0).
      return 0 - zone.utcOffset(ms);
    }
  }
  return intlOffsetMinutes(timezone, ms);
}

/**
 * Decalage du fuseau par rapport a UTC, en minutes, positif a l'est
 * (Europe/Paris en hiver : 60). `at` : instant de reference (defaut : maintenant).
 */
export function getTimezoneOffsetMinutes(timezone: string, at: InstantInput = Date.now()): number {
  const ms = toMs(at);
  if (Number.isNaN(ms)) return 0;
  return offsetMinutesAt(timezone, ms);
}

/** Decalage au format ISO : "+01:00", "+00:00", "-05:00". */
export function formatTimezoneOffset(timezone: string, at: InstantInput = Date.now()): string {
  const minutes = getTimezoneOffsetMinutes(timezone, at);
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

// ---------------------------------------------------------------------------
// Instant UTC -> heure murale
// ---------------------------------------------------------------------------

function wallClockFromShiftedMs(shiftedMs: number): WallClock {
  const d = new Date(shiftedMs);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    millisecond: d.getUTCMilliseconds(),
  };
}

/**
 * Heure murale du fuseau a l'instant donne. Retourne `null` si l'instant est
 * invalide.
 */
export function toWallClock(instant: InstantInput, timezone: string): WallClock | null {
  const ms = toMs(instant);
  if (Number.isNaN(ms)) return null;
  return wallClockFromShiftedMs(ms + offsetMinutesAt(timezone, ms) * MS_PER_MINUTE);
}

// ---------------------------------------------------------------------------
// Heure murale -> instant UTC
// ---------------------------------------------------------------------------

const WALL_CLOCK_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/;

/** Parse "YYYY-MM-DD" ou "YYYY-MM-DDTHH:mm[:ss[.SSS]]" (sans fuseau). */
export function parseWallClock(value: string): WallClock | null {
  const match = WALL_CLOCK_PATTERN.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second, fraction] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour ?? 0),
    minute: Number(minute ?? 0),
    second: Number(second ?? 0),
    millisecond: fraction ? Number(fraction.padEnd(3, '0')) : 0,
  };
}

/**
 * Instant UTC correspondant a une heure murale saisie dans un fuseau.
 *
 * Cas limites des changements d'heure (choix assumes, identiques a ceux de
 * `date-fns-tz` et du mode "compatible" de Temporal) :
 * - heure DOUBLEE (retour a l'heure d'hiver, ex. Paris 02:30 le dernier
 *   dimanche d'octobre) : on prend la PREMIERE occurrence (heure d'ete) ;
 * - heure INEXISTANTE (passage a l'heure d'ete, ex. Paris 02:30 le dernier
 *   dimanche de mars) : on decale vers l'avant de la duree du saut
 *   (02:30 -> 03:30 heure d'ete), on ne rejette pas la saisie.
 *
 * Retourne un `Date` invalide si l'entree n'est pas exploitable.
 */
export function fromWallClock(wall: WallClock | string, timezone: string): Date {
  const parsed = typeof wall === 'string' ? parseWallClock(wall) : wall;
  if (!parsed) return new Date(NaN);
  const wallAsUtc = Date.UTC(
    parsed.year,
    parsed.month - 1,
    parsed.day,
    parsed.hour,
    parsed.minute,
    parsed.second,
    parsed.millisecond
  );
  if (Number.isNaN(wallAsUtc)) return new Date(NaN);

  // Decalages en vigueur nettement avant et nettement apres : au plus une
  // transition dans l'intervalle.
  const offsetBefore = offsetMinutesAt(timezone, wallAsUtc - MS_PER_DAY);
  const offsetAfter = offsetMinutesAt(timezone, wallAsUtc + MS_PER_DAY);

  const candidates = [offsetBefore, offsetAfter]
    .map((offset) => ({ offset, ms: wallAsUtc - offset * MS_PER_MINUTE }))
    .filter(({ offset, ms }) => offsetMinutesAt(timezone, ms) === offset)
    .map(({ ms }) => ms);

  if (candidates.length > 0) {
    // Heure doublee : deux instants valides -> le plus ancien.
    return new Date(Math.min(...candidates));
  }
  // Heure inexistante : on applique le decalage d'avant la bascule.
  return new Date(wallAsUtc - offsetBefore * MS_PER_MINUTE);
}

// ---------------------------------------------------------------------------
// "Maintenant" et "aujourd'hui" dans un fuseau
// ---------------------------------------------------------------------------

/** Heure murale courante du fuseau. `now` : instant de reference (defaut : horloge locale). */
export function nowInTimezone(timezone: string, now: InstantInput = Date.now()): WallClock {
  return toWallClock(now, timezone) ?? wallClockFromShiftedMs(Date.now());
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** Jour civil (`YYYY-MM-DD`) d'une heure murale. */
export function wallClockToNaiveDate(wall: WallClock): string {
  return `${String(wall.year).padStart(4, '0')}-${pad2(wall.month)}-${pad2(wall.day)}`;
}

/** Jour civil (`YYYY-MM-DD`) du fuseau a l'instant donne. */
export function naiveDateInTimezone(instant: InstantInput, timezone: string): string | null {
  const wall = toWallClock(instant, timezone);
  return wall ? wallClockToNaiveDate(wall) : null;
}

/** "Aujourd'hui" (`YYYY-MM-DD`) dans le fuseau. */
export function todayInTimezone(timezone: string, now: InstantInput = Date.now()): string {
  return wallClockToNaiveDate(nowInTimezone(timezone, now));
}

/** Heure decimale de la journee (0 <= h < 24) du fuseau a l'instant donne. */
export function hourOfDayInTimezone(instant: InstantInput, timezone: string): number | null {
  const wall = toWallClock(instant, timezone);
  if (!wall) return null;
  return wall.hour + wall.minute / 60 + wall.second / 3600;
}
